"use client";

import { useState, useEffect } from "react";
import { fetchWithAuth } from '@/lib/fetch-with-auth';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";
import { auth } from "@/lib/firebase";
import { Loader2, Activity, RefreshCw, Music2 } from "lucide-react";

/** Measure key + BPM with real DSP (chromagram → Krumhansl-Schmuckler), so the
 *  key never depends on an LLM's ear. Runs in the browser: free, deterministic. */
async function measureKeyAndBpm(url: string) {
  const { decodeAudioFromUrl, analyzeAudioBuffer } = await import("@/lib/audio-analysis");
  const buf = await decodeAudioFromUrl(url);
  return analyzeAudioBuffer(buf);
}

/** Save the DSP-corrected key/BPM back onto the work (or release) doc so the
 *  track page, DJ decks and Camelot wheel all read the same values. */
async function persistMeasured(id: string, isRelease: boolean | undefined, a: any) {
  const { doc, updateDoc } = await import("firebase/firestore");
  const { db } = await import("@/lib/firebase");
  const patch: Record<string, any> = { musicAnalysis: a };
  if (a.keyName) patch.key = a.keyName;
  if (a.bpm) patch.bpm = a.bpm;
  await updateDoc(doc(db, isRelease ? "releases" : "works", id), patch);
}

// ── Types ─────────────────────────────────────────────────────────────────────

export interface MusicAnalysis {
  bpm: number | null;
  bpmConfidence?: "high" | "medium" | "low";
  keyRoot: string | null;
  keyMode: "major" | "minor" | "other" | null;
  keyName: string | null;
  timeSignature: string | null;
  mainChords: string[];
  sections: { name: string; chords: string[] }[];
  energy: "low" | "medium" | "high" | null;
  mood: string | null;
  mainInstruments: string[];
  analysedAt?: string;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function BpmDial({ bpm }: { bpm: number }) {
  const tempo =
    bpm < 70  ? "Very Slow" :
    bpm < 100 ? "Slow"      :
    bpm < 120 ? "Moderate"  :
    bpm < 140 ? "Upbeat"    :
    bpm < 170 ? "Fast"      : "Very Fast";

  return (
    <div className="flex flex-col items-center justify-center px-4 py-2 rounded-xl bg-foreground/[0.04] border border-border/50">
      <span className="text-3xl font-bold font-mono leading-none">{bpm}</span>
      <span className="text-[10px] font-semibold tracking-widest uppercase text-muted-foreground mt-0.5">BPM</span>
      <span className="text-[10px] text-muted-foreground/60 mt-0.5">{tempo}</span>
    </div>
  );
}

function Pill({ label, dimmed }: { label: string; dimmed?: boolean }) {
  return (
    <span
      className={cn(
        "inline-flex items-center px-2 py-0.5 rounded-md border text-xs font-mono font-medium",
        dimmed
          ? "border-border/40 text-muted-foreground/60 bg-transparent"
          : "border-border/70 text-foreground bg-foreground/[0.04]"
      )}
    >
      {label}
    </span>
  );
}

function Arrow() {
  return <span className="text-muted-foreground/30 text-xs select-none">→</span>;
}

function ChordRow({ chords }: { chords: string[] }) {
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {chords.map((chord, i) => (
        <div key={i} className="flex items-center gap-1.5">
          <Pill label={chord} />
          {i < chords.length - 1 && <Arrow />}
        </div>
      ))}
    </div>
  );
}

function EnergyDots({ level }: { level: "low" | "medium" | "high" }) {
  const filled = level === "low" ? 1 : level === "medium" ? 2 : 3;
  return (
    <span className="flex items-center gap-0.5">
      {[1, 2, 3].map(i => (
        <span
          key={i}
          className={cn(
            "inline-block w-2 h-2 rounded-full",
            i <= filled ? "bg-primary" : "bg-border"
          )}
        />
      ))}
    </span>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────

interface MusicAnalysisPanelProps {
  /** Pre-existing analysis loaded from Firestore. */
  initialAnalysis?: MusicAnalysis | null;
  /** Audio URL to send to the API. */
  audioUrl?: string;
  /** Firestore document ID (work or release). */
  workId: string;
  /** True when the item is a release (saves to releases/ not works/). */
  isRelease?: boolean;
  /** Track title for better context in the prompt. */
  title?: string;
  /** Artist name for context. */
  artist?: string;
  /** If false, hides the Analyze / Re-analyze button. */
  canAnalyze?: boolean;
  /** Additional class names for the outer wrapper. */
  className?: string;
}

export function MusicAnalysisPanel({
  initialAnalysis,
  audioUrl,
  workId,
  isRelease = false,
  title,
  artist,
  canAnalyze = false,
  className,
}: MusicAnalysisPanelProps) {
  const [analysis, setAnalysis] = useState<MusicAnalysis | null>(initialAnalysis ?? null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Keep in sync if parent refreshes item
  useEffect(() => {
    setAnalysis(initialAnalysis ?? null);
  }, [initialAnalysis]);

  const handleAnalyze = async () => {
    if (!audioUrl || loading) return;
    setLoading(true);
    setError(null);
    try {
      // analyze-track requires a verified Firebase ID token (requireAuth).
      const token = await auth.currentUser?.getIdToken();
      if (!token) {
        setError("Please sign in to analyse.");
        setLoading(false);
        return;
      }
      const res = await fetchWithAuth("/api/ai/analyze-track", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ audioUrl, workId, isRelease, title, artist }),
      });
      const data = await res.json();
      if (data.analysis) {
        const ai = data.analysis as MusicAnalysis;
        // KEY + BPM come from real DSP, not the model's ear. Gemini guessed
        // Am on a C#m track (Gordon); a chromagram + Krumhansl-Schmuckler
        // correlation is deterministic and doesn't hallucinate. The AI keeps
        // the parts DSP can't do (sections, chords, mood, instruments).
        let measured: Awaited<ReturnType<typeof measureKeyAndBpm>> | null = null;
        try { measured = await measureKeyAndBpm(audioUrl); } catch { /* AI values stand */ }
        const merged: MusicAnalysis = { ...ai };
        if (measured?.key && measured.keyConfidence >= 0.35) {
          merged.keyName = measured.key;
          merged.keyRoot = measured.key.split(" ")[0];
          merged.keyMode = measured.keyMode;
          (merged as any).keySource = "dsp";
          (merged as any).keyConfidence = measured.keyConfidence;
        }
        if (measured?.bpm && measured.bpm > 0) merged.bpm = measured.bpm;
        setAnalysis(merged);
        // Persist the corrected values so the track page/DJ decks agree.
        if (workId && (measured?.key || measured?.bpm)) {
          void persistMeasured(workId, isRelease, merged).catch(() => {});
        }
      } else {
        setError(data.error ?? "Analysis failed");
      }
    } catch (err: any) {
      setError(err.message ?? "Analysis failed");
    } finally {
      setLoading(false);
    }
  };

  const hasAudio = !!audioUrl;

  return (
    <div className={cn("space-y-4", className)}>

      {/* Header row */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 text-sm font-semibold">
          <Activity className="h-4 w-4 text-primary" />
          Music Analysis
        </div>
        {canAnalyze && hasAudio && (
          <Button
            size="sm"
            variant="outline"
            className="h-7 text-xs gap-1.5"
            onClick={handleAnalyze}
            disabled={loading}
          >
            {loading ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : analysis ? (
              <RefreshCw className="h-3 w-3" />
            ) : (
              <Activity className="h-3 w-3" />
            )}
            {loading ? "Analysing…" : analysis ? "Re-analyse" : "Analyse Track"}
          </Button>
        )}
      </div>

      {/* Error */}
      {error && (
        <p className="text-xs text-destructive bg-destructive/10 rounded-lg px-3 py-2">{error}</p>
      )}

      {/* Loading skeleton */}
      {loading && !analysis && (
        <div className="flex flex-col gap-3 animate-pulse">
          <div className="h-16 rounded-xl bg-foreground/[0.04] border border-border/50" />
          <div className="h-4 w-3/4 rounded bg-foreground/[0.04]" />
          <div className="h-4 w-1/2 rounded bg-foreground/[0.04]" />
        </div>
      )}

      {/* Empty state */}
      {!analysis && !loading && !error && (
        <div className="flex flex-col items-center justify-center gap-2 py-6 text-center rounded-xl border border-dashed border-border/50">
          <Music2 className="h-8 w-8 text-muted-foreground/30" />
          <p className="text-xs text-muted-foreground">
            {canAnalyze
              ? hasAudio ? "No analysis yet — click Analyse Track to run." : "No audio file attached."
              : "Music analysis not yet available for this track."}
          </p>
        </div>
      )}

      {/* Analysis display */}
      {analysis && (
        <div className="space-y-4">

          {/* Top stat row */}
          <div className="flex items-stretch gap-3 flex-wrap">
            {/* BPM */}
            {analysis.bpm != null && <BpmDial bpm={analysis.bpm} />}

            {/* Key + Time sig */}
            <div className="flex flex-col justify-center gap-2 flex-1 min-w-[120px] px-4 py-2 rounded-xl bg-foreground/[0.04] border border-border/50">
              {analysis.keyName && (
                <div className="flex items-center gap-2">
                  <span className="text-[10px] uppercase tracking-wider text-muted-foreground w-8">Key</span>
                  <span className="text-sm font-semibold">{analysis.keyName}</span>
                  {analysis.keyMode && (
                    <Badge variant="secondary" className="text-[9px] px-1 py-0 h-4 capitalize">
                      {analysis.keyMode}
                    </Badge>
                  )}
                  {(analysis as any).keySource === "dsp" && (
                    <Badge
                      className="text-[9px] px-1 py-0 h-4 bg-emerald-600 text-white"
                      title={`Measured from the audio (chromagram · Krumhansl-Schmuckler)${
                        (analysis as any).keyConfidence
                          ? ` — confidence ${Math.round((analysis as any).keyConfidence * 100)}%`
                          : ""
                      }`}
                    >
                      measured
                    </Badge>
                  )}
                </div>
              )}
              {analysis.timeSignature && (
                <div className="flex items-center gap-2">
                  <span className="text-[10px] uppercase tracking-wider text-muted-foreground w-8">Time</span>
                  <span className="text-sm font-mono font-semibold">{analysis.timeSignature}</span>
                </div>
              )}
              {analysis.energy && (
                <div className="flex items-center gap-2">
                  <span className="text-[10px] uppercase tracking-wider text-muted-foreground w-8">Energy</span>
                  <EnergyDots level={analysis.energy} />
                  <span className="text-xs text-muted-foreground capitalize">{analysis.energy}</span>
                </div>
              )}
            </div>
          </div>

          {/* Mood */}
          {analysis.mood && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <span className="uppercase tracking-wider text-[10px]">Mood</span>
              <span className="text-foreground">{analysis.mood}</span>
            </div>
          )}

          {/* Chord progression by section */}
          {analysis.sections && analysis.sections.length > 0 && (
            <>
              <Separator />
              <div className="space-y-2.5">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Chord Progression
                </p>
                {analysis.sections.map((section, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <span className="text-[10px] uppercase tracking-wide text-muted-foreground/60 w-16 flex-shrink-0 pt-0.5">
                      {section.name}
                    </span>
                    <ChordRow chords={section.chords} />
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Main chords (if no sections) */}
          {(!analysis.sections || analysis.sections.length === 0) &&
            analysis.mainChords && analysis.mainChords.length > 0 && (
            <>
              <Separator />
              <div className="space-y-1.5">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Chords
                </p>
                <ChordRow chords={analysis.mainChords} />
              </div>
            </>
          )}

          {/* Instruments */}
          {analysis.mainInstruments && analysis.mainInstruments.length > 0 && (
            <>
              <Separator />
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Instruments</span>
                {analysis.mainInstruments.map((inst, i) => (
                  <Pill key={i} label={inst} dimmed />
                ))}
              </div>
            </>
          )}

          {/* Footer timestamp */}
          {analysis.analysedAt && (
            <p className="text-[10px] text-muted-foreground/40 pt-1">
              Analysed {new Date(analysis.analysedAt).toLocaleDateString()}
              {analysis.bpmConfidence && analysis.bpmConfidence !== "high" && (
                <span className="ml-2">· BPM confidence: {analysis.bpmConfidence}</span>
              )}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
